<template>
  <div>
    <table-toolbar
      delete-title="Eliminar carritos"
      :message="
        ordersToDeleteCount > 0
          ? `Carritos seleccionados ${ordersToDeleteCount}`
          : ''
      "
      :search="search"
      :delete-value="deleteActionEnabled"
      :hide-delete="hideDeleteOption"
      hide-export
      @update:search="(e) => (search = e)"
      @update:delete="setDeleteActionStatus"
      @refresh="loadRecentOrders()"
    >
      <template #custom-message>
        <v-radio-group
          v-model="tableFilter"
          class="mt-0 py-md-2 pt-0 pb-2"
          row
          hide-details
        >
          <v-radio
            v-for="filter in tableFilters"
            :key="filter.value"
            color="black"
            :label="filter.label"
            :value="filter.value"
          />
        </v-radio-group>
      </template>
    </table-toolbar>

    <recent-orders-table
      v-model="ordersToDelete"
      :items="filteredOrders"
      :search="search"
      :loading="loadingRecentOrders"
      :hide-headers="headersToHide"
      :deletable="deleteActionEnabled"
      :sort-by="processedFilterActive ? 'processedAt' : undefined"
      height="auto"
      paginated
      @onSelect="selectOrder"
    >
      <template #footer-append>
        <v-scroll-x-reverse-transition>
          <v-btn
            v-if="ordersToDeleteCount > 0"
            class="red-button mt-2 mt-sm-0 w-xs-100"
            :disabled="mutuatingRecentOrders || loadingRecentOrders"
            @click="showDeleteConfirmation = true"
          >
            Eliminar
          </v-btn>
        </v-scroll-x-reverse-transition>
      </template>
    </recent-orders-table>

    <delete-order-confirmation-modal
      v-model="showDeleteConfirmation"
      hide-form
      bar-title="Eliminar carritos"
      :deleting="mutuatingRecentOrders"
      :title="deleteConfirmationTitle"
    >
      <template slot="footer">
        <v-btn
          class="red-button"
          :disabled="mutuatingRecentOrders"
          @click="confirmDelete"
        >
          Confirmar
        </v-btn>
      </template>
    </delete-order-confirmation-modal>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { Channels } from '@/core/constants/channelConstants';
  import { Roles } from '@/core/constants/roleConstants';
  import Policies from '@/core/policies';

  import numbersFormats from '@/mixins/numbersFormats';
  import RecentOrdersTable from '@/components/tables/RecentOrdersTable.vue';
  import TableToolbar from '@/components/tables/TableToolbar.vue';
  import DeleteOrderConfirmationModal from '@/components/modals/DeleteOrderConfirmationModal.vue';

  export default {
    name: 'RecentOrders',

    components: {
      RecentOrdersTable,
      TableToolbar,
      DeleteOrderConfirmationModal,
    },

    mixins: [numbersFormats],

    props: {
      customerCode: {
        type: String,
        default: null,
      },
    },

    data() {
      return {
        search: '',
        ordersToDelete: [],
        deleteActionEnabled: false,
        deleteOrderData: null,
        showDeleteConfirmation: false,

        tableFilter: 'unprocessed',

        tableFilters: [
          { value: 'unprocessed', label: 'Sin procesar' },
          { value: 'processed', label: 'Procesados' },
        ],
      };
    },
    computed: {
      ...mapGetters('recentOrders', [
        'loadingRecentOrders',
        'unprocessedRecentOrders',
        'processedRecentOrders',
        'selectedRecentOrder',
        'mutuatingRecentOrders',
        'recentOrdersMutuationError',
      ]),

      ...mapGetters('auth', ['channel', 'mainRole']),

      ordersToDeleteCount() {
        return this.ordersToDelete.length;
      },

      deleteConfirmationTitle() {
        const listOfOrders = this.ordersToDelete
          .map((item) => item.name)
          .join(', ');

        return this.ordersToDeleteCount === 1
          ? `Vas a eliminar el carrito: ${listOfOrders}`
          : `Vas a eliminar los carritos: ${listOfOrders}`;
      },

      unprocessedFilterActive() {
        return this.tableFilter === 'unprocessed';
      },

      processedFilterActive() {
        return this.tableFilter === 'processed';
      },

      hideDeleteOption() {
        return (
          this.processedFilterActive || !Policies.OrderPolicy.canDeleteAny()
        );
      },

      filteredOrders() {
        const baseOrders = this.unprocessedFilterActive
          ? this.unprocessedRecentOrders
          : this.processedRecentOrders;

        if (this.customerCode) {
          const customer = this.removeLeftZeros(this.customerCode);

          return baseOrders.filter(
            (o) => this.removeLeftZeros(o.customerCode) === customer
          );
        }

        return baseOrders;
      },

      headersToHide() {
        const headersToHide = this.unprocessedFilterActive
          ? ['status', 'processedAt']
          : ['select', 'remainingExpirationDays'];

        if (!Policies.OrderPolicy.canCreate()) {
          headersToHide.push('select');
        }

        switch (this.mainRole.name) {
          case Roles.CUSTOMER_WHOLESALER:
          case Roles.CUSTOMER_RETAIL:
            headersToHide.push(
              'customer',
              'seller',
              'manager',
              'regionalManager',
              'director',
              'subdirector'
            );
            break;
          case Roles.SELLER:
            headersToHide.push(
              'seller',
              'manager',
              'regionalManager',
              'director',
              'subdirector'
            );
            break;
          case Roles.MANAGER:
          case Roles.SUB_MANAGER:
            headersToHide.push(
              'manager',
              'regionalManager',
              'director',
              'subdirector'
            );
            break;
          case Roles.REGIONAL_MANAGER:
            headersToHide.push('regionalManager', 'director', 'subdirector');
            break;
          case Roles.SUB_DIRECTOR:
            headersToHide.push('director', 'subdirector');
            break;
          case Roles.DIRECTOR:
            headersToHide.push('director');
            break;
        }

        return headersToHide;
      },
    },
    watch: {
      tableFilter(newValue) {
        if (newValue === 'processed') {
          this.deleteActionEnabled = false;
          this.ordersToDelete = [];
        }
      },
    },
    methods: {
      ...mapActions('recentOrders', [
        'deleteRecentOrders',
        'setRecentOrderSelected',
        'setRecentOrderSelected',
        'loadRecentOrders',
      ]),

      ...mapActions('loader', ['activeLoader', 'desactiveLoader']),

      async selectOrder(order) {
        this.activeLoader();
        await this.setRecentOrderSelected(order.id);

        if (this.recentOrdersMutuationError) {
          this.$toast.error(this.recentOrdersMutuationError);
        } else {
          this.navigateToOrderCart(order);
        }
        this.desactiveLoader();
      },

      setDeleteActionStatus(enabled) {
        if (enabled) {
          this.deleteActionEnabled = true;
        } else {
          this.deleteActionEnabled = false;
          this.ordersToDelete = [];
        }
      },

      async confirmDelete() {
        const orderIds = this.ordersToDelete.map((item) => item.id);

        await this.deleteRecentOrders(orderIds);
        if (!this.recentOrdersMutuationError) {
          this.$toast.success('Órdenes eliminadas correctamente');
        } else {
          this.$toast.error(this.recentOrdersMutuationError);
        }

        this.showDeleteConfirmation = false;
        this.ordersToDelete = [];
      },

      navigateToOrderCart(order) {
        if (this.channel === Channels.WHOLESALER) {
          this.$router.push({
            name: 'NewShoppingCartPR',
            params: { doctype: order.doctype, idq: 0 },
          });
        } else {
          this.$router.push({
            name: 'NewShoppingCart',
            params: { doctype: order.doctype, idq: 0 },
          });
        }
      },
    },
  };
</script>
